import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Package, ArrowRight, CreditCard, Truck, Mail, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { API } from "../App";
import axios from "axios";

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const orderNumber = searchParams.get("order_number");
  const total = searchParams.get("total");
  const txnId = searchParams.get("txn");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(!!orderId);

  useEffect(() => {
    if (!orderId) return;
    const fetchOrder = async () => {
      try {
        const res = await axios.get(`${API}/orders/${orderId}`);
        setOrder(res.data);
      } catch {
        // Order lookup failed — show basic confirmation from URL params
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  const displayNumber = order?.order_number || orderNumber || orderId?.slice(0, 12);
  const displayTotal = order?.total ?? (total ? parseFloat(total) : null);
  const items = order?.items || [];

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4" data-testid="checkout-success-page">
      <div className="max-w-2xl mx-auto">
        {/* Success Header */}
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center mb-6">
          <div className="flex justify-center mb-5">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 mb-2" data-testid="success-title">
            Order Placed Successfully!
          </h1>
          <p className="text-gray-600 mb-1">Thank you for shopping with Brendolly's Auto Parts.</p>
          <p className="text-sm text-gray-500">A confirmation email has been sent to your inbox.</p>
        </div>

        {/* Order Details Card */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          {/* Order Number Banner */}
          <div className="bg-gray-900 text-white p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 opacity-70" />
              <div>
                <p className="text-xs opacity-60 uppercase tracking-wide">Order Number</p>
                <p className="font-mono text-lg font-bold" data-testid="order-number">{displayNumber}</p>
              </div>
            </div>
            {txnId && (
              <div className="text-right">
                <p className="text-xs opacity-60 uppercase tracking-wide">Transaction ID</p>
                <p className="font-mono text-sm" data-testid="txn-id">{txnId}</p>
              </div>
            )}
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
              <p className="text-sm text-gray-500 mt-2">Loading order details...</p>
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Items */}
              {items.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Items Ordered</h3>
                  <div className="divide-y divide-gray-100">
                    {items.map((item, idx) => (
                      <div key={idx} className="py-3 flex items-center justify-between" data-testid={`success-item-${idx}`}>
                        <div className="flex items-center gap-3">
                          {item.product_image && (
                            <img src={item.product_image} alt="" className="w-12 h-12 rounded border object-cover" />
                          )}
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{item.product_name || item.name || "Product"}</p>
                            <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                          </div>
                        </div>
                        <p className="font-semibold text-gray-900 text-sm">
                          ${((item.unit_price || item.price || 0) * (item.quantity || 1)).toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Totals */}
              <div className="border-t border-gray-200 pt-4 space-y-2" data-testid="success-totals">
                {order && (
                  <>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Subtotal</span>
                      <span>${(order.subtotal || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Shipping</span>
                      <span>${(order.shipping_amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Tax</span>
                      <span>${(order.tax_amount || 0).toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-200">
                  <span>Total Charged</span>
                  <span data-testid="success-total">${displayTotal != null ? displayTotal.toFixed(2) : "—"}</span>
                </div>
              </div>

              {/* Payment & Shipping Info */}
              <div className="grid sm:grid-cols-2 gap-4">
                {order?.payment_status && (
                  <div className="p-4 bg-green-50 border border-green-100 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <CreditCard className="w-4 h-4 text-green-600" />
                      <span className="text-xs font-semibold text-green-800 uppercase">Payment</span>
                    </div>
                    <p className="text-sm font-medium text-green-900 capitalize">{order.payment_status}</p>
                    {order.payment_card_type && (
                      <p className="text-xs text-green-700 mt-0.5">
                        {order.payment_card_type} {order.payment_last_four ? `****${order.payment_last_four}` : ""}
                      </p>
                    )}
                  </div>
                )}

                {order?.shipping_address && (
                  <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Truck className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-semibold text-blue-800 uppercase">Ships To</span>
                    </div>
                    <p className="text-sm font-medium text-blue-900">{order.customer_name}</p>
                    <p className="text-xs text-blue-700">
                      {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* What's Next */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">What happens next?</h3>
          <div className="space-y-3 text-sm text-gray-600">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Mail className="w-3 h-3 text-green-600" />
              </div>
              <p>You'll receive an order confirmation email with your receipt and order details.</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Package className="w-3 h-3 text-blue-600" />
              </div>
              <p>We'll prepare and ship your order. You'll receive a tracking email once shipped.</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Truck className="w-3 h-3 text-purple-600" />
              </div>
              <p>Track your delivery via UPS with the tracking number provided in the shipping email.</p>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-2">Need help with your order?</h3>
          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
            <a href="tel:6173354166" className="flex items-center gap-2 hover:text-primary">
              <Phone className="w-4 h-4" /> (617) 335-4166
            </a>
            <a href="mailto:support@brendollysautoparts.com" className="flex items-center gap-2 hover:text-primary">
              <Mail className="w-4 h-4" /> support@brendollysautoparts.com
            </a>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/shop">
            <Button variant="outline" className="w-full sm:w-auto" data-testid="continue-shopping-btn">
              Continue Shopping
            </Button>
          </Link>
          <Link to="/">
            <Button className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white flex items-center gap-2" data-testid="back-home-btn">
              Back to Home <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
